import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Modal, Input, message } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getRegulatoryReports, approveReport, rejectReport } from '../../services/regulatoryReportingService';
import ReportDetailModal from './ReportDetailModal';

const { TextArea } = Input;

const REPORT_TYPE_LABELS = {
  CBN_RETURNS: 'CBN Returns',
  NDIC_RETURNS: 'NDIC Returns',
  AML_CTR: 'AML — CTR',
  AML_SAR: 'AML — SAR',
  AML_STR: 'AML — STR',
  NFIU_REPORT: 'NFIU Report',
  EFCC_REPORT: 'EFCC Compliance',
  CREDIT_BUREAU: 'Credit Bureau',
  CAPITAL_ADEQUACY: 'Capital Adequacy',
  LIQUIDITY_REPORT: 'Liquidity Report',
  PRUDENTIAL_RETURNS: 'Prudential Returns',
  RISK_ASSESSMENT: 'Risk Assessment',
  KYC_COMPLIANCE: 'KYC Compliance',
  TRANSACTION_MONITORING: 'Transaction Monitoring',
  CUSTOM: 'Custom',
};

const ApprovalQueueTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [rejectingReport, setRejectingReport] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPendingReports = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const res = await getRegulatoryReports({ page: page - 1, size, status: 'PENDING_APPROVAL' });
      if (res?.status) {
        setReports(res.data?.content || []);
        setPagination({ current: page, pageSize: size, total: res.data?.totalElements || 0 });
      }
    } catch (err) {
      console.error('Failed to fetch pending reports:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingReports();
  }, [fetchPendingReports]);

  const handleTableChange = (pag) => fetchPendingReports(pag.current, pag.pageSize);

  const handleApprove = async (e, record) => {
    e.stopPropagation();
    setActionLoading(true);
    try {
      const res = await approveReport(record.id);
      if (res?.status) {
        message.success('Report approved successfully');
        fetchPendingReports(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || 'Approval failed');
      }
    } catch (err) {
      message.error('Approval failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (e, record) => {
    e.stopPropagation();
    setRejectingReport(record);
    setRejectionReason('');
    setRejectVisible(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      message.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const res = await rejectReport(rejectingReport.id, { reason: rejectionReason });
      if (res?.status) {
        message.success('Report rejected');
        setRejectVisible(false);
        fetchPendingReports(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || 'Rejection failed');
      }
    } catch (err) {
      message.error('Rejection failed');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: 'Report Name',
      dataIndex: 'reportName',
      key: 'reportName',
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{name}</span>,
    },
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      key: 'reportType',
      render: (type) => (
        <Tag
          style={{
            background: '#f5f5f5',
            color: '#595959',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            fontWeight: 500,
          }}
        >
          {REPORT_TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Reporting Period',
      key: 'period',
      render: (_, record) => (
        <span style={{ color: '#595959', fontSize: 12 }}>
          {record.startDate && record.endDate
            ? `${moment(record.startDate).format('MMM DD')} — ${moment(record.endDate).format('MMM DD, YYYY')}`
            : '—'}
        </span>
      ),
    },
    {
      title: 'Generated By',
      dataIndex: 'generatedBy',
      key: 'generatedBy',
      render: (name) => <span style={{ fontWeight: 500, color: '#333' }}>{name || '—'}</span>,
    },
    {
      title: 'Generated',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      width: 170,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY • hh:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <div className="reg-action-row" onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            style={{ borderRadius: 6, fontSize: 11 }}
            onClick={() => { setSelectedReport(record); setDetailVisible(true); }}
          />
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            className="reg-action-approve"
            style={{ height: 24, fontSize: 11, padding: '0 10px' }}
            loading={actionLoading}
            onClick={(e) => handleApprove(e, record)}
          >
            Approve
          </Button>
          <Button
            size="small"
            icon={<CloseCircleOutlined />}
            className="reg-action-reject"
            style={{ height: 24, fontSize: 11, padding: '0 10px' }}
            onClick={(e) => handleRejectClick(e, record)}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="reg-table-card">
        <Table
          columns={columns}
          dataSource={reports}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} pending`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => { setSelectedReport(record); setDetailVisible(true); },
          })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'reg-row-even' : 'reg-row-odd')}
          className="reg-table"
        />
      </div>

      <ReportDetailModal
        visible={detailVisible}
        onClose={() => setDetailVisible(false)}
        record={selectedReport}
        onRefresh={() => fetchPendingReports(pagination.current, pagination.pageSize)}
      />

      {/* ── Rejection Reason Modal ──────────────── */}
      <Modal
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            Reject Report
          </span>
        }
        open={rejectVisible}
        onCancel={() => setRejectVisible(false)}
        onOk={handleRejectSubmit}
        okText="Reject Report"
        okButtonProps={{
          loading: actionLoading,
          danger: true,
          style: { borderRadius: 8, fontWeight: 700 },
        }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        className="reg-form-modal"
        centered
        destroyOnClose
      >
        <p style={{ color: '#595959', marginBottom: 12 }}>
          Please provide a reason for rejecting <strong>{rejectingReport?.reportName}</strong>:
        </p>
        <TextArea
          rows={4}
          placeholder="Enter rejection reason..."
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>
    </div>
  );
};

export default ApprovalQueueTab;
